const destinations = [
  {
    name: "Costa Rica & Central America",
    context: "UF IFAS field research, UF global health missions, and student volunteer programs draw Gainesville travelers to Costa Rica, Guatemala, Honduras, and Nicaragua regularly. These are grant-funded trips with fixed departure dates",
    urgency: "Research grant timelines cannot flex — departure dates set by funding agencies are non-negotiable",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "United Kingdom",
    context: "London and Oxford draw UF law students for moot court competitions, exchange programs, and academic conferences. UF faculty attend British academic conferences year-round",
    urgency: "Academic conference acceptance letters often arrive 4–6 weeks before the event — standard processing rarely makes this window",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Germany & Europe",
    context: "European research collaborations from UF's College of Engineering and Agricultural programs. UF has exchange partnerships with universities in Germany, Spain, France, and the Netherlands",
    urgency: "Exchange program enrollment confirmation often comes with 8-week departure windows — just barely enough for standard processing",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Africa (Research & Global Health)",
    context: "UF IFAS and UF College of Medicine participate in field research programs in Kenya, Uganda, Tanzania, and West Africa. These are typically humanitarian and research-focused trips on NGO or grant timelines",
    urgency: "Global health and humanitarian mission timelines are set by the host organization — they do not wait for passport renewals",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Mexico & Caribbean",
    context: "Spring break destinations for UF students heading to Cancun, Cozumel, and Jamaica. Family leisure travel for Gainesville families heading to all-inclusive resorts during holiday and summer breaks",
    urgency: "Spring break bookings peak in January — standard processing cannot accommodate March international departures",
    icon: "ri-map-pin-2-line",
  },
  {
    name: "Canada",
    context: "Academic conferences, faculty exchange visits, and Toronto or Vancouver business travel. Many UF faculty have Canadian academic connections from graduate training or joint research grants",
    urgency: "Conference invitations often arrive 4–6 weeks before the event — not enough time for standard processing",
    icon: "ri-map-pin-2-line",
  },
];

const stats = [
  { stat: "69%", label: "of Gainesville expedited requests involve UF-affiliated research, medical, or academic travel" },
  { stat: "6–8 Weeks", label: "typical study-abroad and research program confirmation-to-departure window at UF" },
  { stat: "340 Miles", label: "distance to either the Miami or Atlanta regional passport agency from Gainesville" },
  { stat: "48 Hours", label: "shortest research field trip emergency we have accommodated for a UF IFAS researcher" },
];

export default function GainesvilleDestinationsSection() {
  return (
    <section className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Gainesville Travelers Go — And Why Passports Get Urgent
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Gainesville's passport demand is university-driven — researchers, global health volunteers, study-abroad students, and UF faculty travel internationally on academic and grant-funded schedules that have non-negotiable departure dates and no regional passport agency within practical driving distance.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Florida Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We serve travelers across North Central Florida and the entire state with the same expedited passport expertise.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/tallahassee" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Tallahassee <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/jacksonville" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Jacksonville <i className="ri-arrow-right-line text-xs"></i>
              </a>
              <a href="/cities/orlando" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">
                Orlando <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}